import { api, tokenGet } from "./api";
import { restsToday } from "./quests";
import { useHudStore } from "../store/hudStore";
import { ATTR_CAP, ATTR_DOMAIN, ATTRS, isoToday } from "../data/seed";
import type { Quest, AttrDef, MainQuest, Account, Income, Expense, Debt, DebtPayment, Person, MoodDay, Cert, Game, UpcomingIncome, UpcomingExpense, UpcomingDebt, StandingCost, Outlook } from "../types";

// The live data layer, ported from app.html goLive()/applyX(). Each applyX writes
// the store via setState (the "lib/* read/write the store" pattern). On the live
// path these REPLACE the empty seed with real data or empty. Seed never leaks.
type ApiRec = Record<string, any>;

function applyCharacter(rec: ApiRec | null): void {
  if (!rec) return;
  const st = useHudStore.getState();
  const patch: Partial<ReturnType<typeof useHudStore.getState>> = {
    level: rec.level ?? st.level,
    xp: rec.xp_into ?? rec.xp ?? st.xp,
    xpToday: rec.xp_today ?? rec.xpToday ?? 0,
    streak: rec.streak ?? st.streak,
    lastActive: rec.last_active ?? st.lastActive,
    securedOn: rec.secured_on ?? st.securedOn,
    claimed: rec.claimed_today ?? st.claimed,
    badgeIds: Array.isArray(rec.badges) ? rec.badges : [],
    live: true,
  };
  if (Array.isArray(rec.attributes)) {
    patch.liveAttrs = rec.attributes.map((a: ApiRec): AttrDef => {
      const name = ATTR_CAP[a.name] || a.name;
      const base = ATTRS.find((x) => x.name === name);
      return {
        name,
        color: base?.color || "var(--accent)",
        icon: base?.icon ?? "",
        tint: base?.tint ?? "",
        level: a.level,
        into: a.into,
        next: a.to_next,
      };
    });
  }
  useHudStore.setState(patch);
}

// Reassigns the mains list (goals). Called before applyQuests so quest→main
// resolution uses real goals. Empty on failure so live never shows seed goals.
function applyGoals(list: ApiRec[] | undefined): void {
  useHudStore.setState({ mains: (Array.isArray(list) ? list : []) as MainQuest[] });
}

// Quest steps aren't sent by /api/quests. They live as `- [ ]` task lines in the
// quest's vault note, which /api/vault already parses into `task` blocks. Backfill
// them here so the HUD's Task breakdown shows the note's checklist; the vault stays
// the source of truth for a quest's sub-tasks.
function stepsFromNote(notes: ApiRec | undefined, key: string | undefined): string[] {
  if (!notes || !key) return [];
  const note = notes[key];
  const blocks: ApiRec[] = note && Array.isArray(note.blocks) ? note.blocks : [];
  return blocks
    .filter((b) => b && b.k === "task" && typeof b.text === "string" && b.text.trim())
    .map((b) => String(b.text).trim());
}

// Does a quest belong on the Today list?
//
// This used to be `q.today === true` alone - a flag only ever written by the MCP's
// create_quest(today: true), never set later and never cleared. Nothing in the
// vault carries it, so the HUD's default range landed on an empty list while every
// open quest sat under Week. Today is now derived from the dates you already keep:
//
//   flagged  - the explicit MCP flag still wins, so create_quest(today: true) works
//   daily    - a daily quest is today's work by definition
//   overdue  - a missed date should nag, not disappear
//   due today
//   undated  - no date means nothing deferred it, so it's on the table now
//
// Dated future work stays under Week and surfaces on its own day. The vault's four
// dailies are all undated, so `daily` is redundant against them today - it's here
// so a daily that ever gets a due date still shows up, rather than hiding until it.
function isTodayQuest(q: ApiRec, today: string): boolean {
  if (q.today === true) return true; // the explicit pin wins, weekend included
  // A weekday-only daily (days: weekdays) rests on the Manila weekend: it is
  // not today's work, so it sits under Week until Monday.
  if (q.quest_type === "daily") return !restsToday({ days: typeof q.days === "string" ? q.days : undefined });
  const due = typeof q.due === "string" ? q.due.trim() : "";
  return !due || due <= today;
}

// `list` is GET /api/quests (open only - the endpoint filters status != done)
// and `doneList` is GET /api/quests?status=done. Both are hydrated: the HUD's
// main-quest page and the mainStat selector report "X of Y quests done" and
// drive the progress bar off `finished / all`, so with only the open list the
// finished count is structurally always 0 and every bar sits at 0% after a
// refresh. The `done` map is likewise seeded here; it used to be in-session
// only, so a completion vanished from the page built to count it on reload.
// Screens that want open work still filter on `done` (Domain, Character,
// visibleQuests), so a longer `quests` array does not leak into those lists.
function applyQuests(list: ApiRec[] | null, notes?: ApiRec, doneList?: ApiRec[] | null): void {
  if (!Array.isArray(list)) return; // endpoint absent/failed → keep current quests
  const mains = useHudStore.getState().mains;
  const todayIso = isoToday(); // once per pass, so a midnight tick can't split the list
  const mainId = (g: unknown): string => {
    if (!g) return "m1";
    if (mains.some((m) => m.id === g)) return String(g);
    const byName = mains.find((m) => m.name.toLowerCase() === String(g).toLowerCase());
    return byName ? byName.id : "m1";
  };
  const toQuest = (q: ApiRec): Quest => ({
    id: String(q.id),
    title: q.title,
    xp: Number(q.xp) || 0,
    attr: ATTR_CAP[q.attribute] || "Career",
    domain: q.domain || ATTR_DOMAIN[q.attribute] || "Career",
    type: q.quest_type || "Side quest",
    tier: q.tier || undefined,
    when: isTodayQuest(q, todayIso) ? "today" : "week",
    days: q.days || undefined,
    ...(() => {
      // Resolve every parent the API sent (ids or names), primary first.
      const raw = Array.isArray(q.mains) && q.mains.length > 0 ? (q.mains as unknown[]) : [q.main];
      const mains = [...new Set(raw.map(mainId))];
      return { main: mains[0] as string, mains };
    })(),
    steps: stepsFromNote(notes, q.note_key),
    pinned: false,
    // Dates from the vault frontmatter. The API sends `due` today; created/updated/
    // done_at pass through when present, else stay blank until edited in-session.
    due: q.due || undefined,
    created: q.created || undefined,
    updated: q.updated || undefined,
    completed: q.done_at || undefined,
    notePath: q.note_key || undefined,
  });
  const open = list.map(toQuest);
  const finished = Array.isArray(doneList) ? doneList.map(toQuest) : [];
  const done: Record<string, boolean> = {};
  const awarded: Record<string, number> = {};
  for (const q of finished) {
    done[q.id] = true;
    awarded[q.id] = q.xp;
  }
  // Step ticks persist on the quest record (steps_done) now, so rebuild the check
  // map from the server on every fetch - a reload or tab-switch keeps them. The
  // backend already emptied a recurring checklist's ticks if the month rolled over.
  const stepDone: Record<string, boolean> = {};
  for (const q of list) {
    const sd = Array.isArray(q.steps_done) ? q.steps_done : [];
    for (const i of sd) stepDone[String(q.id) + ":" + Number(i)] = true;
  }
  useHudStore.setState({ quests: [...open, ...finished], done, awarded, stepDone });
}

function applyFinances(f: ApiRec | undefined): void {
  if (f === undefined) {
    useHudStore.setState({ accounts: [], incomes: [], expenses: [], payments: [], transfers: [], debts: [], debtPayments: [], upcomingIncome: [], upcomingExpense: [], upcomingDebt: [], standing: [], outlook: null });
    return;
  }
  useHudStore.setState({
    accounts: (Array.isArray(f.accounts) ? f.accounts : []) as Account[],
    incomes: (Array.isArray(f.incomes) ? f.incomes : []) as Income[],
    expenses: (Array.isArray(f.expenses) ? f.expenses : []) as Expense[],
    // The normalized history includes every actual debt payment and deduplicates
    // old ledger records. An empty history is authoritative, not a fallback.
    payments: Array.isArray(f.paymentHistory) ? f.paymentHistory : Array.isArray(f.payments) ? f.payments : [],
    transfers: Array.isArray(f.transfers) ? f.transfers : [],
    debts: (Array.isArray(f.debts) ? f.debts : []) as Debt[],
    debtPayments: (Array.isArray(f.debtPayments) ? f.debtPayments : []) as DebtPayment[],
    upcomingIncome: (Array.isArray(f.upcomingIncome) ? f.upcomingIncome : []) as UpcomingIncome[],
    upcomingExpense: (Array.isArray(f.upcomingExpense) ? f.upcomingExpense : []) as UpcomingExpense[],
    upcomingDebt: (Array.isArray(f.upcomingDebt) ? f.upcomingDebt : []) as UpcomingDebt[],
    standing: (Array.isArray(f.standing) ? f.standing : []) as StandingCost[],
    outlook: (f.outlook ?? null) as Outlook | null,
  });
}

// Certifications, the exams behind The Golden Jacket. Empty on failure so the
// Learning page shows no module rather than a stale or seeded one.
function applyCerts(list: ApiRec[] | undefined): void {
  useHudStore.setState({ certs: (Array.isArray(list) ? list : []) as Cert[] });
}

// The game backlog, which the backlog-slayer badge counts.
function applyGames(list: ApiRec[] | undefined): void {
  useHudStore.setState({ games: (Array.isArray(list) ? list : []) as Game[] });
}

function applyPeople(list: ApiRec[] | undefined): void {
  useHudStore.setState({ people: (Array.isArray(list) ? list : []) as Person[] });
}

function applyHealth(h: ApiRec | undefined): void {
  useHudStore.setState({
    moodWeek: (Array.isArray(h?.moodWeek) ? h!.moodWeek : []) as MoodDay[],
    bodyWeek: Array.isArray(h?.bodyWeek) ? h!.bodyWeek : [],
    mindCare: Array.isArray(h?.mindCare) ? h!.mindCare : [],
  });
}

// `null` means the request failed; `undefined` can't happen but is treated the
// same. A successful response with no files is a genuinely empty vault, which is
// a different thing and says so in the UI.
//
// `notes` holds every body the listing's size budget allowed (in practice all of
// them). Anything it skipped is fetched on click by openNote and cached into this
// same map.
function applyVault(v: ApiRec | null | undefined): void {
  if (v && v.files) useHudStore.setState({ vaultTree: v.files, vaultNotes: v.notes || {}, vaultLive: true, vaultFailed: false, vaultLoading: false });
  else useHudStore.setState({ vaultFailed: true, vaultLoading: false });
}

// A sign-out, failed startup or newer refresh retires every pending result from
// the previous run. Token renewal may change the token while requests are in
// flight, so the generation identifies the run and token presence identifies
// the signed-in session.
let liveRun = 0;
let vaultRun = 0;
let financeRun = 0;
export function cancelLiveLoads(): void { liveRun++; vaultRun++; }

type StepBaseline = Map<string, { steps: string[]; path: string | undefined }>;
function stepBaseline(): StepBaseline {
  return new Map(useHudStore.getState().quests.map((q) => [q.id, { steps: q.steps, path: q.notePath }]));
}

function applyVaultSteps(v: ApiRec | null, baseline: StepBaseline): void {
  applyVault(v);
  if (!v?.notes) return;
  // Only hydrate checklists that have not been edited since core data arrived.
  // Titles, completion flags, new/deleted quests and the current route belong
  // to the current store, never to the earlier startup response.
  useHudStore.setState((st) => ({
    quests: st.quests.map((q) => {
      const before = baseline.get(q.id);
      if (!before || q.steps !== before.steps || q.notePath !== before.path || !q.notePath || !v.notes[q.notePath]) return q;
      return { ...q, steps: stepsFromNote(v.notes, q.notePath) };
    }),
  }));
}

async function fetchVault(): Promise<ApiRec | null> {
  return api<ApiRec>("/api/vault").catch((e) => { console.warn("/api/vault unavailable:", e.message); return null; });
}

export async function refreshVault(): Promise<void> {
  const run = liveRun;
  const listing = ++vaultRun;
  const baseline = stepBaseline();
  useHudStore.setState({ vaultLoading: true, vaultFailed: false });
  const vault = await fetchVault();
  if (run !== liveRun || listing !== vaultRun || !tokenGet()) return;
  applyVaultSteps(vault, baseline);
}

// Re-pull just the finances slice and re-apply it, used after a write (e.g.
// logging a loan payment) so debts/rows/balances reflect the server truth.
export async function refreshFinances(): Promise<boolean> {
  const run = liveRun;
  const refresh = ++financeRun;
  const f = await api<ApiRec>("/api/finances").catch((e) => { console.warn("/api/finances refresh failed:", e.message); return undefined; });
  if (f === undefined || run !== liveRun || refresh !== financeRun || !tokenGet()) return false;
  applyFinances(f);
  return true;
}

// `keepScreen` is for the refetch that happens when a backgrounded tab comes
// back. Landing on the HUD is right when this runs as the way into the app, and
// wrong when it runs underneath someone already reading a page: it would throw
// them back to the home screen for the crime of switching tabs.
export async function goLive(keepScreen = false): Promise<void> {
  const run = ++liveRun;
  const listing = ++vaultRun;
  const current = () => run === liveRun && !!tokenGet();
  useHudStore.setState({ vaultLoading: true, vaultFailed: false });
  // Start the slower S3 listing alongside core data, but do not make opening
  // the mission console wait for every note. Its rejection is handled now,
  // including when it completes before core data or core startup fails.
  const vaultRequest = fetchVault();
  try {
    const [character, quests, questsDone, finances, people, health, goals, certs, games] = await Promise.all([
      api<ApiRec>("/api/character"),
      api<ApiRec[]>("/api/quests").catch((e) => { console.warn("/api/quests unavailable:", e.message); return null; }),
      // Completed quests, for the main-quest counts and bars. A failure here is not
      // fatal: the board still shows open work, it just cannot report completions.
      api<ApiRec[]>("/api/quests?status=done").catch((e) => { console.warn("/api/quests?status=done unavailable:", e.message); return null; }),
      api<ApiRec>("/api/finances").catch((e) => { console.warn("/api/finances unavailable:", e.message); return undefined; }),
      api<ApiRec[]>("/api/relationships").catch((e) => { console.warn("/api/relationships unavailable:", e.message); return undefined; }),
      api<ApiRec>("/api/health").catch((e) => { console.warn("/api/health unavailable:", e.message); return undefined; }),
      api<ApiRec[]>("/api/goals").catch((e) => { console.warn("/api/goals unavailable:", e.message); return undefined; }),
      api<ApiRec[]>("/api/certs").catch((e) => { console.warn("/api/certs unavailable:", e.message); return undefined; }),
      api<ApiRec[]>("/api/games").catch((e) => { console.warn("/api/games unavailable:", e.message); return undefined; }),
    ]);
    if (!current()) return;
    applyCharacter(character);
    applyGoals(goals); // before applyQuests: quest→main resolution uses real goals
    // Keep existing checklists during a focus refresh; fresh note bodies backfill
    // them after the listing arrives.
    applyQuests(quests, useHudStore.getState().vaultNotes ?? undefined, questsDone);
    applyFinances(finances);
    applyPeople(people);
    applyCerts(certs);
    applyGames(games);
    applyHealth(health);
    if (!keepScreen) useHudStore.setState({ screen: "hud" });
    const baseline = stepBaseline();
    void vaultRequest.then((vault) => {
      if (!current() || listing !== vaultRun) return;
      applyVaultSteps(vault, baseline);
    });
  } catch (e) {
    if (current()) {
      cancelLiveLoads();
      useHudStore.setState({ vaultLoading: false });
    }
    throw e;
  }
}
